// ControllerSocket — the one-and-only WebSocket client every screen
// uses to talk to the controller. Never construct a browser WebSocket
// outside this package; the ESLint config in web-ui makes that a
// build-blocker.
//
// Behaviors (PROTOCOL.md §8 + §9):
//   - HTTP token from AuthClient is replayed in `node.register`.
//   - Subscriptions are deduplicated and replayed on reconnect.
//   - RPCs (anything with a `correlationId`) resolve when the matching
//     response arrives; `internalRequest` returns a Promise.
//   - On disconnect, in-flight RPCs reject with a typed error so
//     callers can retry rather than hang forever.
//   - Reconnect uses ReconnectStrategy (exponential backoff capped at
//     ~30s); reset() runs on a successful handshake.
//   - Token refresh: callers proactively refresh via AuthClient and
//     pass the new token in via `setToken`; on `401`-style register
//     rejections the socket emits an `auth.required` error.

import { mintDeliveryKey, PROTOCOL_VERSION, type Role } from '@usability-suite/schema';
import { AuthClient, type AuthSession } from './auth';
import { ErrorBus } from './error_bus';
import { helmDisplayName, helmUserId, setHelmUserId } from './identity';
import {
  DEFAULT_RETRY_POLICY,
  ReceiptImpl,
  type PublishReceipt,
  type ReceiptRetryPolicy,
  type ReceiptStatus,
} from './publish_receipt';
import {
  ReconnectStrategy,
  withDefaults,
  type ReconnectOptions,
} from './reconnect_strategy';
import { topicMatches } from './topic_matcher';
import {
  browserTransportFactory,
  type Transport,
  type TransportFactory,
} from './transport';

export type { PublishReceipt, ReceiptRetryPolicy, ReceiptStatus };

export interface From {
  nodeId: string;
  role: Role;
  sessionId?: string | undefined;
  userId?: string | undefined;
  displayName?: string | undefined;
}

export interface To {
  scope: 'controller' | 'session' | 'role' | 'node' | 'participant' | 'broadcast';
  sessionId?: string | undefined;
  role?: Role | undefined;
  nodeId?: string | undefined;
  participantId?: string | undefined;
}

export interface Envelope {
  v: number;
  type: string;
  /** Per-publish delivery key — see PROTOCOL.md §5a. */
  key: string;
  /** Retry counter; absent on first publish, ≥1 on retries. */
  attempt?: number | undefined;
  correlationId?: string | undefined;
  from: From;
  to?: To | undefined;
  /**
   * Sender-stamped wall-clock at emit (ms since the Unix epoch).
   * Required on every daemon-emitted envelope, optional on browser
   * envelopes (browsers are exempt from the daemon clock-sync
   * handshake — see PROTOCOL.md §12). The wire field name is
   * `tAgent`; the legacy `tSession` (session-relative seconds) is
   * retired.
   */
  tAgent?: number | undefined;
  payload: unknown;
}

export interface SocketOptions {
  url: string;
  origin?: string; // for fetch() to /api/auth; default = same origin as page
  nodeId: string;
  role: Role;
  reconnect?: ReconnectOptions;
  transport?: TransportFactory;
  /** Used for setTimeout; tests inject a fake. */
  scheduler?: { setTimeout: typeof setTimeout; clearTimeout: typeof clearTimeout };
}

export interface PublishedEnvelope {
  type: string;
  payload: unknown;
  to?: To;
  /** Optional override; defaults to opts.role. */
  fromSessionId?: string | undefined;
  /**
   * Override the default retry policy for this publish. Per-message
   * tuning ("chat is impatient; session.start is patient") goes here.
   */
  retry?: ReceiptRetryPolicy;
}

export interface RpcOptions {
  /** ms before the RPC rejects with a timeout. Default 10s. */
  timeoutMs?: number;
}

export type EnvelopeListener = (e: Envelope) => void;

interface PendingRpc {
  resolve: (e: Envelope) => void;
  reject: (err: Error) => void;
  timeoutHandle: ReturnType<typeof setTimeout>;
}

interface SubscriptionEntry {
  count: number;
}

export class ControllerSocket {
  private transport: Transport | null = null;
  private readonly factory: TransportFactory;
  private readonly reconnect: ReconnectStrategy;
  private readonly scheduler: NonNullable<SocketOptions['scheduler']>;
  private session: AuthSession | null = null;
  private connected = false;
  private registered = false;
  private stopped = false;
  // Populated from the latest node.register response; empty before
  // the first successful register. Callers consult this to disable
  // UI surfaces whose backing feature the server didn't declare,
  // instead of branching on which topology is on the other end.
  private serverFeatures: string[] = [];

  private readonly subs = new Map<string, SubscriptionEntry>();
  private readonly listeners = new Set<EnvelopeListener>();
  private readonly stateListeners = new Set<() => void>();
  private readonly pending = new Map<string, PendingRpc>();
  /** Outstanding delivery-receipt ledger keyed by envelope `key`. */
  private readonly receipts = new Map<string, ReceiptImpl>();
  /** Last-sent envelope per receipt key — replayed verbatim on retry. */
  private readonly receiptEnvelopes = new Map<string, Envelope>();
  private correlationSeq = 0;
  private readonly auth: AuthClient;
  private readonly errors = new ErrorBus();
  private reconnectHandle: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly opts: SocketOptions) {
    this.factory = opts.transport ?? browserTransportFactory;
    this.reconnect = new ReconnectStrategy(withDefaults(opts.reconnect));
    // Bind to globalThis so window.setTimeout's "this must be window"
    // check doesn't throw "Illegal invocation" when we invoke through
    // the scheduler indirection.
    this.scheduler = opts.scheduler ?? {
      setTimeout: globalThis.setTimeout.bind(globalThis),
      clearTimeout: globalThis.clearTimeout.bind(globalThis),
    };
    this.auth = new AuthClient(opts.origin ?? '');
  }

  // -----------------------------------------------------------------
  // Public surface
  // -----------------------------------------------------------------

  setToken(session: AuthSession): void {
    this.session = session;
  }

  /** Begin connecting. Idempotent. */
  start(): void {
    if (this.stopped) return;
    if (this.transport) return;
    this.openSocket();
  }

  /** Stop and tear down. After stop() the socket will not reconnect. */
  stop(): void {
    this.stopped = true;
    if (this.reconnectHandle !== null) {
      this.scheduler.clearTimeout(this.reconnectHandle);
      this.reconnectHandle = null;
    }
    this.transport?.close(1000, 'stop');
    this.transport = null;
    this.connected = false;
    this.registered = false;
    this.failPending(new Error('socket stopped'));
  }

  isConnected(): boolean {
    return this.connected;
  }

  isRegistered(): boolean {
    return this.registered;
  }

  /** Subscribe to a topic pattern. Reference-counted; returns an unsubscribe handle. */
  subscribe(pattern: string): () => void {
    const existing = this.subs.get(pattern);
    if (existing) {
      existing.count += 1;
    } else {
      this.subs.set(pattern, { count: 1 });
      if (this.registered) {
        this.sendSubscribe([pattern]);
      }
    }
    return () => this.releaseSubscription(pattern);
  }

  /** Listen to every inbound envelope. Use topic-matching helpers to filter. */
  onMessage(listener: EnvelopeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Notified whenever connected/registered transitions. */
  onStateChange(listener: () => void): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  errorBus(): ErrorBus {
    return this.errors;
  }

  /**
   * Publish an event. Returns a {@link PublishReceipt} that flips from
   * `pending` → `sent` when the broker echoes the envelope back to
   * this sender (PROTOCOL.md §5a). Callers that don't care about
   * delivery can ignore the return value — `publish()` is still
   * effectively fire-and-forget.
   */
  publish(p: PublishedEnvelope): PublishReceipt {
    const env = this.makeEnvelope(p, undefined);
    const policy = p.retry ?? DEFAULT_RETRY_POLICY;
    const receipt = new ReceiptImpl(
      env.key,
      {
        resend: (attempt: number) => this.resendReceipt(env.key, attempt),
        release: () => {
          this.receipts.delete(env.key);
          this.receiptEnvelopes.delete(env.key);
        },
      },
      policy,
      this.scheduler,
    );
    this.receipts.set(env.key, receipt);
    this.receiptEnvelopes.set(env.key, env);
    if (!this.connected) {
      this.errors.emit({
        source: 'transport',
        code: 'not_connected',
        detail: 'publish before connect',
      });
      // Receipt's retry timer will catch this and resend once we reconnect;
      // for now, surface "failed" promptly if we never come back.
      return receipt;
    }
    this.transport!.send(JSON.stringify(env));
    return receipt;
  }

  private resendReceipt(key: string, attempt: number): void {
    const original = this.receiptEnvelopes.get(key);
    if (!original) return;
    const env: Envelope = { ...original, attempt };
    this.receiptEnvelopes.set(key, env);
    if (!this.connected) {
      this.errors.emit({
        source: 'transport',
        code: 'not_connected',
        detail: `retry ${attempt} of ${original.type} before connect`,
      });
      return;
    }
    this.transport!.send(JSON.stringify(env));
  }

  /** Round-trip RPC. Resolves with the response envelope; rejects on timeout/disconnect/error. */
  request(p: PublishedEnvelope, opts: RpcOptions = {}): Promise<Envelope> {
    if (!this.connected) {
      return Promise.reject(new Error('socket not connected'));
    }
    const correlationId = 'r-' + ++this.correlationSeq;
    const timeoutMs = opts.timeoutMs ?? 10_000;
    return new Promise<Envelope>((resolve, reject) => {
      const timeoutHandle = this.scheduler.setTimeout(() => {
        this.pending.delete(correlationId);
        reject(new Error('rpc timeout: ' + p.type));
      }, timeoutMs);
      this.pending.set(correlationId, { resolve, reject, timeoutHandle });
      const env = this.makeEnvelope(p, correlationId);
      this.transport!.send(JSON.stringify(env));
    });
  }

  /** Snapshot of the current subscription set; useful for tests. */
  subscriptionPatterns(): string[] {
    return [...this.subs.keys()].sort();
  }

  // -----------------------------------------------------------------
  // Auth helpers
  // -----------------------------------------------------------------

  authClient(): AuthClient {
    return this.auth;
  }

  // -----------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------

  private openSocket(): void {
    const t = this.factory(this.opts.url, (e) => {
      if (e.type === 'open') this.handleOpen();
      else if (e.type === 'message') this.handleMessage(e.data);
      else if (e.type === 'close') this.handleClose(e.code, e.reason);
      else if (e.type === 'error') this.handleError(e.reason);
    });
    this.transport = t;
  }

  private emitStateChange(): void {
    for (const l of this.stateListeners) {
      try {
        l();
      } catch {
        // listener errors are not allowed to break state propagation
      }
    }
  }

  private handleOpen(): void {
    this.connected = true;
    this.emitStateChange();
    // Send node.register; the controller acks via correlationId.
    const correlationId = 'reg-' + ++this.correlationSeq;
    const payload: Record<string, unknown> = { kind: 'ui' };
    if (this.session) payload.token = this.session.token;
    // Stable identity (persisted in localStorage). The server is
    // free to override either field in the register response — see
    // handleRegisterResponse for the adopt path.
    payload.userId = helmUserId();
    const name = helmDisplayName();
    if (name) payload.displayName = name;
    const env: Envelope = {
      v: PROTOCOL_VERSION,
      type: 'node.register',
      key: mintDeliveryKey(),
      correlationId,
      from: this.fromForOutbound(),
      payload,
    };
    this.pending.set(correlationId, {
      resolve: (e) => this.handleRegisterResponse(e),
      reject: (err) =>
        this.errors.emit({
          source: 'auth',
          code: 'register_failed',
          detail: err.message,
        }),
      timeoutHandle: this.scheduler.setTimeout(() => {
        const p = this.pending.get(correlationId);
        if (p) {
          this.pending.delete(correlationId);
          p.reject(new Error('register timeout'));
        }
      }, 10_000),
    });
    this.transport!.send(JSON.stringify(env));
  }

  private handleRegisterResponse(env: Envelope): void {
    if (env.type === 'error') {
      // Server error envelope shape (schema/messages/error.ts):
      //   { code, message, retryable, requestType?, fields?, cause? }
      const payload = (env.payload as { code?: string; message?: string }) ?? {};
      const code = payload.code ?? 'register_failed';
      const detail = payload.message ?? JSON.stringify(env.payload);
      this.errors.emit({
        source: 'auth',
        code,
        detail,
        envelope: env,
      });
      return;
    }
    this.registered = true;
    this.reconnect.reset();
    // Adopt the server's user-id if it returned one (always present
    // on a successful response; protocol contract). If the request
    // omitted userId the server minted a fresh one and the client
    // persists it for the next reconnect.
    const respPayload = (env.payload as { userId?: unknown; features?: unknown }) ?? {};
    if (typeof respPayload.userId === 'string' && respPayload.userId.length > 0) {
      setHelmUserId(respPayload.userId);
    }
    if (Array.isArray(respPayload.features)) {
      this.serverFeatures = respPayload.features.filter(
        (f): f is string => typeof f === 'string',
      );
    }
    // Replay subscriptions.
    if (this.subs.size > 0) {
      this.sendSubscribe([...this.subs.keys()]);
    }
    this.emitStateChange();
  }

  /** Server-advertised feature flags from the latest register response.
   *  Empty array before the first successful register. */
  features(): readonly string[] {
    return this.serverFeatures;
  }

  hasFeature(name: string): boolean {
    return this.serverFeatures.includes(name);
  }

  private sendSubscribe(topics: string[]): void {
    const env: Envelope = {
      v: PROTOCOL_VERSION,
      type: 'subscribe',
      key: mintDeliveryKey(),
      correlationId: 'sub-' + ++this.correlationSeq,
      from: this.fromForOutbound(),
      payload: { topics },
    };
    this.transport!.send(JSON.stringify(env));
  }

  private sendUnsubscribe(topics: string[]): void {
    if (!this.connected) return;
    const env: Envelope = {
      v: PROTOCOL_VERSION,
      type: 'unsubscribe',
      key: mintDeliveryKey(),
      correlationId: 'usb-' + ++this.correlationSeq,
      from: this.fromForOutbound(),
      payload: { topics },
    };
    this.transport!.send(JSON.stringify(env));
  }

  /** Build the `from` field every outbound envelope carries. Stamps
   *  the per-installation user-id and optional display name from
   *  identity.ts so chat / markers / audit rows survive reconnects.
   *  Inlined for envelopes that touch `sessionId` (publish-time
   *  fields layer over this shape). */
  private fromForOutbound(): From {
    const from: From = {
      nodeId: this.opts.nodeId,
      role: this.opts.role,
      userId: helmUserId(),
    };
    const name = helmDisplayName();
    if (name) from.displayName = name;
    return from;
  }

  private releaseSubscription(pattern: string): void {
    const e = this.subs.get(pattern);
    if (!e) return;
    e.count -= 1;
    if (e.count <= 0) {
      this.subs.delete(pattern);
      this.sendUnsubscribe([pattern]);
    }
  }

  private handleMessage(data: string): void {
    let env: Envelope;
    try {
      env = JSON.parse(data) as Envelope;
    } catch (err) {
      this.errors.emit({
        source: 'envelope',
        code: 'parse_failed',
        detail: (err as Error).message,
      });
      return;
    }
    // Resolve pending RPC if correlationId matches.
    if (env.correlationId) {
      const pending = this.pending.get(env.correlationId);
      if (pending) {
        this.pending.delete(env.correlationId);
        this.scheduler.clearTimeout(pending.timeoutHandle);
        pending.resolve(env);
        return;
      }
    }
    // Delivery receipt: when the broker echoes back an envelope we
    // sent (same `key`, same `from.nodeId`), flip the pending receipt
    // to `sent`. The envelope is also dispatched to subscribers below
    // so UI handlers see their own publishes too — the receipt is
    // about delivery confirmation, not about hiding the event.
    if (env.key && env.from.nodeId === this.opts.nodeId) {
      const receipt = this.receipts.get(env.key);
      if (receipt) receipt.markSent();
    }
    // Otherwise treat as an event/broadcast.
    for (const l of this.listeners) {
      try {
        l(env);
      } catch (err) {
        this.errors.emit({
          source: 'envelope',
          code: 'listener_threw',
          detail: (err as Error).message,
        });
      }
    }
  }

  private handleClose(code: number, reason: string): void {
    this.connected = false;
    this.registered = false;
    this.transport = null;
    this.emitStateChange();
    this.failPending(new Error(`socket closed ${code} ${reason}`));
    if (this.stopped) return;
    this.scheduleReconnect();
  }

  private handleError(reason: string): void {
    this.errors.emit({ source: 'transport', code: 'transport_error', detail: reason });
  }

  private scheduleReconnect(): void {
    const delay = this.reconnect.nextDelayMs();
    this.reconnectHandle = this.scheduler.setTimeout(() => {
      this.reconnectHandle = null;
      if (this.stopped) return;
      this.openSocket();
    }, delay);
  }

  private failPending(err: Error): void {
    for (const [, pending] of this.pending) {
      this.scheduler.clearTimeout(pending.timeoutHandle);
      pending.reject(err);
    }
    this.pending.clear();
  }

  private makeEnvelope(
    p: PublishedEnvelope,
    correlationId: string | undefined,
  ): Envelope {
    const from = this.fromForOutbound();
    if (p.fromSessionId !== undefined) from.sessionId = p.fromSessionId;
    const env: Envelope = {
      v: PROTOCOL_VERSION,
      type: p.type,
      key: mintDeliveryKey(),
      from,
      // Wire-uniform: every envelope carries the sender's wall-clock
      // at emit (PROTOCOL.md §5 / §12). Browser clients are exempt
      // from the clock-sync handshake but still stamp tAgent so the
      // controller can record `events.t_agent` consistently across
      // every producer.
      tAgent: Date.now(),
      payload: p.payload,
    };
    if (correlationId !== undefined) env.correlationId = correlationId;
    if (p.to !== undefined) env.to = p.to;
    return env;
  }
}

/** Convenience: dispatch an inbound envelope to a single subscription pattern. */
export function envelopeMatchesPattern(env: Envelope, pattern: string): boolean {
  // Topic schemes the controller fans out on (controllerd::route_event).
  const candidates = [env.type];
  if (env.from.sessionId) candidates.push(`session.${env.from.sessionId}.${env.type}`);
  return candidates.some((t) => topicMatches(pattern, t));
}
